import { useState } from 'react';
import MetricSource from '../components/Portfolio/MetricSource';
import Showcase from '../components/Portfolio/Showcase';
import { Link } from 'react-router-dom';
import { figures, RESUME_HREF } from '../data/figures';
import socialData from '../constants/social.json';
import * as S from '../components/Portfolio/styles';

const linkedin = socialData.socialLinks.find(link => link.platform === 'linkedin')?.url;
const github = socialData.socialLinks.find(link => link.platform === 'github')?.url;

export default function Home() {
  const [rebuilt, setRebuilt] = useState(true);
  return (
    <S.Container>
      <S.Hero id="hero" aria-labelledby="portfolio-title">
        <S.HeroGrid>
          <div>
            <S.Kicker>Guilherme Niclewicz · Orlando, FL</S.Kicker>
            <h1 id="portfolio-title">
              Forward deployed engineer.
              <br />
              <em>AI that shows its work.</em>
            </h1>
            <S.Intro>
              I build applied AI where a wrong answer costs something: taxes, residency
              applications, personal documents. Every answer traced to a source, or no answer at
              all. Disney today, Principal Engineer at Olist before.
            </S.Intro>
            <S.Actions>
              <S.ButtonLink href="#work">See what I’ve built ↘</S.ButtonLink>
              <S.TextLink href={RESUME_HREF} download>
                Download CV ↓
              </S.TextLink>
            </S.Actions>
          </div>
          <S.HeroProof>
            <S.Kicker>SHIPPED & OPERATED SOLO / 2025–2026</S.Kicker>
            <div className="hero-number">
              <strong>5</strong>
              <span>
                products.
                <br />
                20 months.
              </span>
            </div>
            <S.ProductMarks aria-label="Visit my products">
              {[
                {
                  icon: 'imgradar-mark.svg',
                  name: 'IMG Radar',
                  href: 'https://imgradar.com',
                  scale: 1,
                },
                { icon: 'findy-mark.svg', name: 'Findy', href: 'https://askfindy.com', scale: 1 },
                {
                  icon: 'chorie-avatar.webp',
                  name: 'HomeChores',
                  href: 'https://homechoresapp.com',
                  scale: 0.92,
                },
                {
                  icon: 'saidacerta-seal-96.png',
                  name: 'Saída Certa',
                  href: 'https://saidacerta.com',
                  scale: 0.9,
                },
                { icon: 'radar407-icon.svg', name: 'radar407', href: '#contact', scale: 1 },
              ].map(({ icon, name, href, scale }) => (
                <S.ProductMark
                  key={name}
                  href={href}
                  $scale={scale}
                  target={href.startsWith('https:') ? '_blank' : undefined}
                  rel={href.startsWith('https:') ? 'noopener noreferrer' : undefined}
                  aria-label={
                    href === '#contact'
                      ? 'radar407 — contact me'
                      : `${name} — official site (opens in a new tab)`
                  }
                >
                  <img src={`/assets/brand/${icon}`} alt="" />
                  <span>
                    {name}
                    {href === '#contact' ? ' · Contact' : ' ↗'}
                  </span>
                </S.ProductMark>
              ))}
            </S.ProductMarks>
            <S.HeroTraction>
              <div>
                <strong>{figures['chorie.publishedUsers'].value}</strong>
                <span>users on Home Chores</span>
              </div>
              <div>
                <strong>{figures['imgradar.subscribers'].value}</strong>
                <span>paying subscribers on IMG Radar in its first month</span>
              </div>
            </S.HeroTraction>
          </S.HeroProof>
        </S.HeroGrid>
        <S.Employers aria-label="Selected experience">
          <span>Engineering at</span>
          <img src="/assets/logos/disney.svg" alt="Disney" />
          <img src="/assets/logos/olist.svg" alt="Olist" />
          <img src="/assets/logos/exxonmobil.svg" alt="ExxonMobil" />
          <img src="/assets/logos/bradesco.svg" alt="Bradesco" />
        </S.Employers>
      </S.Hero>

      <S.Section id="experience" aria-labelledby="experience-title">
        <S.SectionHeading>
          <div>
            <S.Kicker>01 / Systems at scale</S.Kicker>
            <h2 id="experience-title">The work. The before and after.</h2>
          </div>
          <S.TextLink href={RESUME_HREF} download>
            Experience & sources in my CV ↗
          </S.TextLink>
        </S.SectionHeading>
        <S.ImpactGrid>
          <S.ImpactCard $dark>
            <S.ImpactHeader>
              <img src="/assets/logos/olist.svg" alt="Olist" />
              <span>PRINCIPAL ENGINEER / 2022–2025</span>
            </S.ImpactHeader>
            <h3>
              A million messages a day.
              <br />A fraction of the machinery.
            </h3>
            <S.Topology aria-label={rebuilt ? 'After: 2 services' : 'Before: 20 services'}>
              {Array.from({ length: rebuilt ? 2 : 20 }, (_, i) => (
                <span key={i}>{rebuilt ? ['API', 'DELIVERY'][i] : ''}</span>
              ))}
            </S.Topology>
            <S.Controls>
              <button aria-pressed={!rebuilt} onClick={() => setRebuilt(false)}>
                Before / 20 services
              </button>
              <button aria-pressed={rebuilt} onClick={() => setRebuilt(true)}>
                After / 2 services
              </button>
            </S.Controls>
            <S.ImpactNumbers>
              <div>
                <MetricSource id="record.olist.messages" />
                <span>messages / day</span>
              </div>
              <div>
                <MetricSource id="record.olist.delivery" />
                <span>delivery time</span>
              </div>
            </S.ImpactNumbers>
            <p>
              As Principal Engineer for the Growth unit, guiding about{' '}
              {figures['record.olist.engineers'].value} engineers, I led the consolidation of a
              fragmented notification stack into one API for email, SMS, WhatsApp, push and web.
            </p>
          </S.ImpactCard>
          <S.ImpactCard>
            <S.ImpactHeader>
              <img src="/assets/logos/disney.svg" alt="Disney" />
              <span>SENIOR ENGINEER / 2025–NOW · CONTRACT</span>
            </S.ImpactHeader>
            <h3>
              Production changes.
              <br />
              Without the production guesswork.
            </h3>
            <S.Pipeline>
              <span>Replicate</span>
              <b>→</b>
              <span>Validate</span>
              <b>→</b>
              <span>Deploy</span>
            </S.Pipeline>
            <S.BarComparison>
              <div>
                <span>Before</span>
                <i />
              </div>
              <div>
                <span>After</span>
                <i />
              </div>
            </S.BarComparison>
            <S.ImpactNumbers>
              <div>
                <MetricSource id="record.disney.deployCut" />
                <span>less deploy time</span>
              </div>
              <div>
                <MetricSource id="record.disney.engineers" />
                <span>engineers & analysts</span>
              </div>
            </S.ImpactNumbers>
            <p>
              I built ephemeral preview environments for Disney’s data platform, with automated
              validation and policy checks before rollout.
            </p>
          </S.ImpactCard>
        </S.ImpactGrid>
        <S.CareerStrip aria-labelledby="field-title">
          <h3 id="field-title">Forward deployed, before I had the title.</h3>
          <div>
            <div>
              <S.Kicker>EXXONMOBIL / 2020–2022</S.Kicker>
              <strong>
                {figures['record.exxon.ownersTrained'].value} application owners trained.
              </strong>
              <p>
                I stepped up to lead the entitlement engine when senior engineers left, then trained
                app owners across APAC and the US to adopt zero-trust access for{' '}
                {figures['record.exxon.employees'].value} employees and{' '}
                {figures['record.exxon.applications'].value} apps.
              </p>
            </div>
            <div>
              <S.Kicker>OLIST / 2022–2025</S.Kicker>
              <strong>From pitch to production.</strong>
              <p>
                I took initiatives from idea to funding with business and tech leadership, then led
                the build, from web crawling for data products to reshaping transactional data for
                acquisitions.
              </p>
            </div>
            <div>
              <S.Kicker>BRADESCO / 2019–2020</S.Kicker>
              <strong>{figures['record.bradesco.runtimeCut'].value} less processing time.</strong>
              <p>
                When the Hadoop cluster hit its hardware limits, I built a PySpark framework that
                ran {figures['record.bradesco.jobs'].value} data-prep jobs in under 4 hours. Other
                projects in the department adopted it, and I trained the consultant team.
              </p>
            </div>
          </div>
        </S.CareerStrip>
      </S.Section>

      <S.Section id="work" aria-labelledby="work-title">
        <S.SectionHeading>
          <div>
            <S.Kicker>02 / Products I built & run</S.Kicker>
            <h2 id="work-title">From first commit to customers.</h2>
          </div>
          <p>One focused demo per product. Try it here, then explore the live app.</p>
        </S.SectionHeading>
        <Showcase />
      </S.Section>

      <S.Section id="writing" aria-labelledby="writing-title">
        <S.SectionHeading>
          <div>
            <S.Kicker>03 / Writing</S.Kicker>
            <h2 id="writing-title">How I make engineering decisions.</h2>
          </div>
          <S.TextLink as={Link} to="/writing">
            All writing <span aria-hidden="true">↗</span>
          </S.TextLink>
        </S.SectionHeading>
        <S.WritingGrid>
          <a href="/writing/no-passage-no-answer.html">
            <S.Kicker>AI &amp; product judgment · 3 min</S.Kicker>
            <h3>
              The AI I Built to Refuse <span aria-hidden="true">↗</span>
            </h3>
            <p>Why a document assistant should stay silent when it cannot support an answer.</p>
          </a>
          <a href="/writing/keeping-the-loop-honest.html">
            <S.Kicker>Engineering with AI · 16 slides</S.Kicker>
            <h3>
              Keeping the loop honest. <span aria-hidden="true">↗</span>
            </h3>
            <p>The checks that keep an autonomous coding agent from approving its own mistakes.</p>
          </a>
        </S.WritingGrid>
      </S.Section>

      <S.Contact id="contact" aria-labelledby="contact-title">
        <div>
          <S.Kicker>Platforms, applied AI & product engineering</S.Kicker>
          <h2 id="contact-title">Let’s build something that ships.</h2>
          <p>Greater Orlando, FL. M.Sc. in Numerical Methods. Brazilian, from Curitiba.</p>
        </div>
        <S.Actions>
          <S.ButtonLink href={linkedin} target="_blank" rel="noopener noreferrer">
            Connect on LinkedIn <span aria-hidden="true">↗</span>
          </S.ButtonLink>
          <S.TextLink href={github} target="_blank" rel="noopener noreferrer">
            GitHub ↗
          </S.TextLink>
        </S.Actions>
      </S.Contact>
    </S.Container>
  );
}
